import {
    createPageMetricsForResponse,
    PageType,
} from '@jet-app/music-classical/metrics/page';
import { ObjectGraph } from '@jet/environment/dependencies';

import type { AlbumPage } from '@jet-app/music-classical/models/pages/album';
import type {
    ClassicalAPIResponse,
    ClassicalAPIResponseHeader,
    ClassicalSection,
    ClassicalShelfItem,
} from '@jet-app/music-classical/api/types';
import { transformSections } from '@jet-app/music-classical/utilities/transforms/sections';
import { playActionForClassicalPlayAction } from '@jet-app/music-classical/action-model/PlayAction';
import {
    fixArtwork,
    makeVideoArtworkDictionary,
    type PageHeaderType,
    type PageScreenType,
} from '@jet-app/music-classical/models';
import { makeContextualMenuFetchAction } from '@jet-app/music-classical/action-model/contextual-menu/FetchAction';
import { addingAlbumSeoData } from '@jet-app/music-classical/utilities/seo/Album';
import { getDurationFromTracks } from '@jet-app/music-classical/utilities/seo/duration';
import { getSubtitleLink } from '@jet-app/music-classical/utilities/transforms/shelf';
import type { ContextualMenuResponse } from '@jet-app/music-classical/utilities/should-render-contextual-menu';

export function renderAlbumPage(
    canonicalURL: string,
    apiResponse: ClassicalAPIResponse,
    contextualMenuResponse: ContextualMenuResponse,
    objectGraph: ObjectGraph,
    prominentItemIdentifier?: string,
): AlbumPage {
    const responseHeader = apiResponse?.body
        ?.header as unknown as ClassicalAPIResponseHeader;
    const tracks: ClassicalShelfItem[] =
        apiResponse?.body?.sections.reduce(
            (acc: ClassicalShelfItem[], section: ClassicalSection) => {
                if (section.type === 'track-list') {
                    acc.push(
                        ...section.components[0]?.items?.filter(
                            (item) => item.type === 'track',
                        ),
                    );
                }
                return acc;
            },
            [],
        ) ?? [];

    const { entities, renderContextualMenu } = contextualMenuResponse;
    const page = {
        pageKind: 'componentScreen',
        kind: 'AlbumPage',
        screenId: apiResponse?.body.screenId,
        screenType: apiResponse?.body.screenType as PageScreenType,
        header: {
            id: apiResponse?.body.screenId,
            type: responseHeader?.type as PageHeaderType,
            title: responseHeader?.title,
            subtitle: responseHeader.subtitle,
            subtitleLinks: Array.isArray(responseHeader?.subtitleActions)
                ? responseHeader.subtitleActions
                      .map((action) => getSubtitleLink(action, objectGraph))
                      .filter(Boolean)
                : [],
            artwork: fixArtwork(responseHeader.image),
            videoArtwork: makeVideoArtworkDictionary(
                responseHeader.uberArtwork,
            ),
            year: responseHeader?.year,
            modalPresentationDescriptor: !!responseHeader?.editorialNotes
                ?.standard
                ? {
                      headerTitle: responseHeader.editorialNotes.title,
                      headerSubtitle: null, // TODO: See where this value would come from
                      paragraphText: responseHeader.editorialNotes.standard,
                  }
                : null,
            // rdar://150296128 (Album pages 500)
            // There is potential for primary button playParams to have a null value
            primaryButton:
                apiResponse.body.primaryButton?.action?.playParams?.filter(
                    (param) => !!param,
                )?.length
                    ? {
                          title: apiResponse.body.primaryButton?.title,
                          action: playActionForClassicalPlayAction(
                              apiResponse.body.primaryButton?.action,
                          ),
                      }
                    : null,
            contextMenuAction:
                apiResponse?.body?.userComponentsAction && renderContextualMenu
                    ? makeContextualMenuFetchAction(
                          apiResponse?.body?.userComponentsAction.url,
                      )
                    : null,
            shareButton:
                !apiResponse?.body?.userComponentsAction ||
                !renderContextualMenu
                    ? apiResponse?.body?.secondaryButtons?.find(
                          (button) => button.subtype === 'share',
                      )
                    : null,

            // These properties should come from the API so as to stay in sync with tracklist footer data
            // rdar://143762610 ([Classical on Web] Need number of tracks and duration properties for Album, Playlist, and Recording pages)
            trackCount: tracks.length,
            totalDurationMs: getDurationFromTracks(tracks),
            entities,
        },
        sections: transformSections(
            apiResponse?.body?.sections as any,
            objectGraph,
            apiResponse?.body?.screenType,
            apiResponse?.body?.screenId,
            prominentItemIdentifier,
        ),
        canonicalURL,
        pageMetrics: createPageMetricsForResponse(
            apiResponse,
            PageType.artistPage,
        ),
    };

    return addingAlbumSeoData(canonicalURL, page, objectGraph);
}
